import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "@/i18n";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Globe, MapPin, Filter, BadgeCheck } from "lucide-react";
import {
  fetchApprovedPartners,
  PARTNER_CATEGORY_LABEL,
  type BusinessPartner,
  type PartnerCategory,
} from "@/lib/advertising";
import { OfficialPartnerBadge } from "@/components/official-partner-badge";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { SmartImage } from "@/components/smart-image";

const TITLE = "Partner Directory | BidNova Auctions";
const DESCRIPTION =
  "Discover BidNova's trusted partner network — card shops, grading companies, breakers, accessories, shipping and insurance partners.";
const URL = "https://bidnovaauctions.uk/partners";

export const Route = createFileRoute("/partners/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Partners", path: "/partners" }])],
  }),
  component: PartnersIndexPage,
});

const CATEGORY_ORDER: PartnerCategory[] = [
  "card_shop",
  "grading_company",
  "card_accessories",
  "breaker",
  "collectibles_business",
  "shipping_partner",
  "insurance_partner",
  "media_partner",
  "other",
];

function PartnersIndexPage() {
  const { t } = useTranslation("community");
  const { data: partners = [], isLoading } = useQuery({
    queryKey: ["public-partners-all"],
    queryFn: fetchApprovedPartners,
    staleTime: 60_000,
  });

  const [q, setQ] = useState("");
  const [category, setCategory] = useState<PartnerCategory | "all">("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return partners.filter((p) => {
      if (category !== "all" && p.partner_category !== category) return false;
      if (verifiedOnly && !(p.is_verified || p.is_official_partner)) return false;
      if (!term) return true;
      return (
        p.business_name.toLowerCase().includes(term) ||
        (p.description ?? "").toLowerCase().includes(term) ||
        (p.location ?? "").toLowerCase().includes(term)
      );
    });
  }, [partners, q, category, verifiedOnly]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <header className="mb-6 text-center">
        <p className="text-[11px] uppercase tracking-[0.2em] text-gold">{t("partners.index.eyebrow", "Partner Directory")}</p>
        <h1 className="mt-2 font-display text-4xl text-white sm:text-5xl">{t("partners.index.title", "Trusted BidNova partners")}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          {t("partners.index.subtitle", "Every partner below is reviewed and approved by the BidNova team — from card shops and grading companies to shipping and insurance specialists.")}
        </p>
        <div className="mt-4 text-sm">
          <Link to="/partners/apply" className="text-gold underline">{t("partners.index.becomePartner", "Become a partner →")}</Link>
        </div>
      </header>

      <div className="mb-6 grid gap-3 sm:grid-cols-[1fr,auto,auto] sm:items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("partners.index.searchPlaceholder", "Search partners by name, description or location…")}
            className="pl-9"
            aria-label={t("partners.index.searchLabel", "Search partners")}
          />
        </div>
        <button
          type="button"
          onClick={() => setVerifiedOnly((v) => !v)}
          aria-pressed={verifiedOnly}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition",
            verifiedOnly
              ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
              : "border-border/60 text-muted-foreground hover:border-emerald-500/60 hover:text-emerald-200",
          )}
        >
          <BadgeCheck className="h-3.5 w-3.5" aria-hidden /> {t("partners.index.verifiedOnly", "Verified only")}
        </button>
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> {t("partners.index.resultsCount", "{count, plural, one {# result} other {# results}}", { count: filtered.length })}
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        <CategoryChip active={category === "all"} onClick={() => setCategory("all")}>{t("partners.index.all", "All")}</CategoryChip>
        {CATEGORY_ORDER.map((c) => (
          <CategoryChip key={c} active={category === c} onClick={() => setCategory(c)}>
            {PARTNER_CATEGORY_LABEL[c]}
          </CategoryChip>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("partners.index.loading", "Loading partners…")}</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border/60 bg-card p-8 text-center text-muted-foreground">
          {t("partners.index.empty", "No partners match your filters yet.")}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <PartnerCard key={p.id} partner={p} />
          ))}
        </div>
      )}
    </div>
  );
}


function CategoryChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition",
        active
          ? "border-gold bg-gold/10 text-gold"
          : "border-border/60 text-muted-foreground hover:border-gold/60 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function PartnerCard({ partner }: { partner: BusinessPartner }) {
  return (
    <Link
      to="/partners/$slug"
      params={{ slug: partner.slug }}
      className="group flex flex-col gap-3 rounded-2xl border border-border/60 bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.35)]"
    >
      <div className="flex items-start gap-3">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-border/40 bg-black/40">
          {partner.logo_url ? (
            <SmartImage src={partner.logo_url} alt={`${partner.business_name} logo`} width={56} height={56} widths={[56, 112]} resize="contain" className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-display text-xl text-gold">
              {partner.business_name[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="font-display text-lg leading-tight group-hover:text-gold truncate">{partner.business_name}</h3>
            {partner.is_official_partner && <OfficialPartnerBadge compact size="sm" />}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {PARTNER_CATEGORY_LABEL[partner.partner_category]}
          </p>
        </div>
      </div>
      {partner.description && (
        <p className="text-sm text-muted-foreground line-clamp-3">{partner.description}</p>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        {partner.location && (
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{partner.location}</span>
        )}
        {partner.website_url && (
          <Badge variant="outline" className="border-gold/30 text-gold">
            <Globe className="h-3 w-3 mr-1" />Website
          </Badge>
        )}
      </div>
    </Link>
  );
}
